import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Gift, Star, Share2, Calendar, LogOut, Copy, Check,
  ArrowRight, Award, History, ExternalLink, MessageCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logos
const BUSINESS_LOGOS = {
  'The Holland Barbershop': 'https://customer-assets.emergentagent.com/job_cutsync-2/artifacts/sfpd5g0k_The%20Holland.jpeg',
  'Paulo Cisneros': 'https://customer-assets.emergentagent.com/job_cutsync-2/artifacts/7hd0na99_Paulo%20Cisneros.jpeg',
};

export default function UserDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [selectedReward, setSelectedReward] = useState(null);
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('loyalty_token');
    if (!token) {
      navigate('/lealtad/login');
      return;
    }
    fetchData(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('loyalty_token')}` }
  });

  const fetchData = async (token) => {
    try {
      const headers = { headers: { Authorization: `Bearer ${token}` } };
      const [userRes, rewardsRes, transRes] = await Promise.all([
        axios.get(`${API}/loyalty/me`, headers),
        axios.get(`${API}/loyalty/rewards?active_only=true`),
        axios.get(`${API}/loyalty/me/transactions`, headers),
      ]);
      setUser(userRes.data);
      setRewards(rewardsRes.data);
      setTransactions(transRes.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('loyalty_token');
        navigate('/lealtad/login');
      } else {
        toast.error('Error al cargar datos');
      }
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/lealtad/registro?ref=${user.user_code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('¡Link copiado!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareReferral = async () => {
    const link = `${window.location.origin}/lealtad/registro?ref=${user.user_code}`;
    
    // Get business name from user's branch
    let businessName = "este lugar";
    if (user.branch_name) {
      // Try to get full business name from branches
      try {
        const response = await axios.get(`${API}/branches/public`);
        const userBranch = response.data.find(b => b.name === user.branch_name);
        if (userBranch?.business_name) {
          businessName = userBranch.business_name;
        }
      } catch (e) {
        // Use default
      }
    }
    
    const text = `¡Tienes que conocer ${businessName}, a mí me encantó el servicio! Regístrate aquí: ${link}`;
    
    if (navigator.share) {
      navigator.share({ text, url: link });
    } else {
      navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('¡Mensaje copiado!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const redeemReward = async () => {
    if (!selectedReward) return;
    setRedeeming(true);
    
    try {
      const response = await axios.post(
        `${API}/loyalty/redeem/${selectedReward.id}`,
        {},
        getAuthHeaders()
      );
      toast.success(response.data.message);
      setSelectedReward(null);
      fetchData(localStorage.getItem('loyalty_token'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al canjear');
    } finally {
      setRedeeming(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('loyalty_token');
    navigate('/lealtad/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-gold">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="user-dashboard">
      {/* Header */}
      <header className="bg-zinc-900/80 border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src={BUSINESS_LOGOS['The Holland Barbershop']} 
              alt="Logo" 
              className="w-10 h-10 object-contain"
            />
            <div>
              <h1 className="font-heading text-lg text-white">Mi Cuenta</h1>
              <p className="text-xs text-zinc-500">Programa de Lealtad</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            className="text-zinc-400 hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Salir
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* User Code & Points Card */}
        <Card className="bg-gradient-to-br from-gold/20 to-amber-900/20 border-gold/30 p-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <p className="text-zinc-400 text-sm">Hola,</p>
              <h2 className="font-heading text-2xl text-white">{user.name}</h2>
            </div>
            <div className="text-right">
              <p className="text-xs text-zinc-500 uppercase">Tu código</p>
              <p className="font-mono text-2xl text-gold font-bold tracking-widest">{user.user_code}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-black/30 rounded-sm">
              <Star className="w-6 h-6 text-gold mx-auto mb-2" />
              <p className="text-3xl font-heading text-white">{user.total_points}</p>
              <p className="text-xs text-zinc-500">Puntos</p>
            </div>
            <div className="text-center p-4 bg-black/30 rounded-sm">
              <Calendar className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
              <p className="text-3xl font-heading text-white">{user.checkin_count}</p>
              <p className="text-xs text-zinc-500">Visitas</p>
            </div>
            <div className="text-center p-4 bg-black/30 rounded-sm">
              <Share2 className="w-6 h-6 text-blue-500 mx-auto mb-2" />
              <p className="text-3xl font-heading text-white">{user.referral_count}</p>
              <p className="text-xs text-zinc-500">Referidos</p>
            </div>
          </div>
        </Card>

        {/* Actions Row */}
        <div className="grid grid-cols-2 gap-4">
          {/* Referral Button */}
          <Card className="bg-zinc-900/50 border-zinc-800 p-4">
            <div className="flex items-center gap-3 mb-4">
              <Share2 className="w-5 h-5 text-blue-500" />
              <div>
                <h3 className="text-white font-medium">Invita Amigos</h3>
                <p className="text-xs text-zinc-500">Gana puntos por cada referido</p>
              </div>
            </div>
            <Button
              onClick={shareReferral}
              className="w-full bg-blue-500 text-white hover:bg-blue-600"
            >
              {copied ? <Check className="w-4 h-4 mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
              Compartir Link
            </Button>
          </Card>

          {/* Book Appointment Button */}
          <Card className="bg-zinc-900/50 border-zinc-800 p-4">
            <div className="flex items-center gap-3 mb-4">
              <MessageCircle className="w-5 h-5 text-gold" />
              <div>
                <h3 className="text-white font-medium">Agendar Cita</h3>
                <p className="text-xs text-zinc-500">Reserva con nuestro asistente</p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/citas')}
              className="w-full bg-gold text-black hover:bg-gold-dark"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Ir a Agendar
            </Button>
          </Card>
        </div>

        {/* Rewards Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg text-white flex items-center gap-2">
              <Gift className="w-5 h-5 text-purple-500" />
              Recompensas Disponibles
            </h3>
          </div>

          {rewards.length === 0 ? (
            <Card className="bg-zinc-900/50 border-zinc-800 p-8 text-center">
              <Gift className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500">No hay recompensas disponibles</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {rewards.map((reward) => {
                const canRedeem = user.total_points >= reward.points_required;
                const isOutOfStock = reward.stock > 0 && reward.redeemed_count >= reward.stock;
                // Birthday-only match: today's day+month must match the user's registered birthday
                const now = new Date();
                const isUserBirthday = reward.birthday_only
                  ? (now.getDate() === Number(user.birthday_day) && (now.getMonth() + 1) === Number(user.birthday_month))
                  : true;
                const birthdayBlocked = reward.birthday_only && !isUserBirthday;
                const finalCanRedeem = canRedeem && !birthdayBlocked;

                let buttonLabel;
                if (isOutOfStock) buttonLabel = 'Agotado';
                else if (birthdayBlocked) buttonLabel = 'Solo el día de tu cumpleaños';
                else if (canRedeem) buttonLabel = 'Canjear';
                else buttonLabel = `Faltan ${reward.points_required - user.total_points} pts`;

                return (
                  <Card
                    key={reward.id}
                    className={`bg-zinc-900/50 border-zinc-800 overflow-hidden ${(!canRedeem || birthdayBlocked) && 'opacity-60'}`}
                    data-testid={`reward-card-${reward.id}`}
                  >
                    {reward.image_url && (
                      <div className="h-32 bg-zinc-800 relative">
                        <img src={reward.image_url} alt={reward.name} className="w-full h-full object-cover" />
                        {reward.birthday_only && (
                          <span className="absolute top-2 right-2 text-[10px] px-2 py-0.5 rounded-full bg-pink-500/90 text-white font-semibold uppercase tracking-wider" data-testid={`birthday-badge-${reward.id}`}>
                            🎂 Cumpleaños
                          </span>
                        )}
                      </div>
                    )}
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className="font-medium text-white">{reward.name}</h4>
                        {reward.birthday_only && !reward.image_url && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-pink-500/20 text-pink-400 font-semibold uppercase tracking-wider whitespace-nowrap" data-testid={`birthday-badge-inline-${reward.id}`}>
                            🎂 Cumpleaños
                          </span>
                        )}
                      </div>
                      {reward.description && (
                        <p className="text-sm text-zinc-500 mb-2">{reward.description}</p>
                      )}
                      {reward.birthday_only && (
                        <p className="text-[10px] text-pink-400 mb-3" data-testid={`birthday-note-${reward.id}`}>
                          🎂 Solo canjeable el día de tu cumpleaños ({String(user.birthday_day).padStart(2,'0')}/{String(user.birthday_month).padStart(2,'0')})
                          {isUserBirthday && <span className="ml-1 text-green-400">— ¡HOY!</span>}
                        </p>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-gold font-semibold">{reward.points_required} pts</span>
                        <Button
                          size="sm"
                          disabled={!finalCanRedeem || isOutOfStock}
                          onClick={() => setSelectedReward(reward)}
                          className={finalCanRedeem && !isOutOfStock
                            ? "bg-purple-500 text-white hover:bg-purple-600"
                            : "bg-zinc-700 text-zinc-400"
                          }
                          data-testid={`redeem-btn-${reward.id}`}
                        >
                          {buttonLabel}
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Transaction History */}
        <div>
          <h3 className="font-heading text-lg text-white flex items-center gap-2 mb-4">
            <History className="w-5 h-5 text-zinc-500" />
            Historial de Puntos
          </h3>
          
          {transactions.length === 0 ? (
            <Card className="bg-zinc-900/50 border-zinc-800 p-8 text-center">
              <History className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500">Sin movimientos aún</p>
            </Card>
          ) : (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <ScrollArea className="h-[250px]">
                <div className="divide-y divide-zinc-800">
                  {transactions.map((t) => (
                    <div key={t.id} className="p-4 flex items-center justify-between">
                      <div>
                        <p className="text-sm text-white">{t.description}</p>
                        <p className="text-xs text-zinc-500">
                          {new Date(t.created_at).toLocaleDateString('es-MX', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <span className={`text-lg font-semibold ${t.points > 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {t.points > 0 ? '+' : ''}{t.points}
                      </span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </Card>
          )}
        </div>
      </main>

      {/* Redeem Confirmation Dialog */}
      <Dialog open={!!selectedReward} onOpenChange={() => setSelectedReward(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white">
              Confirmar Canje
            </DialogTitle>
          </DialogHeader>
          {selectedReward && (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-zinc-800 rounded-sm">
                <h4 className="text-white font-medium mb-1">{selectedReward.name}</h4>
                <p className="text-gold font-semibold">{selectedReward.points_required} puntos</p>
              </div>
              <p className="text-sm text-zinc-400">
                Después del canje tendrás <span className="text-white">{user.total_points - selectedReward.points_required} puntos</span>.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setSelectedReward(null)}
                  className="flex-1 border-zinc-700"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={redeemReward}
                  disabled={redeeming}
                  className="flex-1 bg-purple-500 text-white hover:bg-purple-600"
                >
                  {redeeming ? 'Canjeando...' : 'Confirmar Canje'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="py-6 text-center border-t border-zinc-800 mt-8">
        <p className="text-zinc-600 text-sm">Programa de Lealtad</p>
      </footer>
    </div>
  );
}
